/**
 * Where the arena's chrome may be, and when it has to get smaller.
 *
 * ── WHY THIS FILE EXISTS: `sm:` STOPPED MEANING "THERE IS ROOM" ──
 *
 * Every panel in the HUD used to shrink on Tailwind's `sm` breakpoint — `min-width: 640px` — and
 * that was right for as long as the small surface was a phone held upright, which is 390 CSS
 * pixels wide. The landscape card turned the arena on its side: the shell is locked to landscape
 * and a browser is asked to rotate, so the phone this HUD is drawn on is now **844 x 390**. Every
 * `sm:` class fires on it. A player got the full desktop layout — a 160px minimap, ten
 * leaderboard rows, eight stat rows and an 80px split button — on a screen 390 pixels tall, which
 * is most of the report this card answers: "we need to reorganize the hud on mobile ... smaller
 * text and things".
 *
 * So the axis that decides is no longer the width alone. `COMPACT_HUD_QUERY` is "either
 * dimension is too small for the full HUD", and it is read as a boolean in React rather than as a
 * CSS variant because the layout *structure* changes with it — which column a panel is in, not
 * just how big it is — and because a rule nobody can render in jsdom is a rule nothing can pin.
 *
 * ── THE ZONES, AND THE HOLE IN THE MIDDLE ──
 *
 * A thumb steers by dragging **around the blob**, and the blob is in the middle of the screen
 * (`pointerSteers`, `scenes/steering.ts`: the heading is `pointer - blob`). So the middle column
 * of the arena, and everything below it, is the one part of the screen the player's hand is
 * actually using — "more area to play without the overlays where I would drag my thumb".
 *
 * `STEERING_ZONE` is that region, and `COMPACT_ZONE_RECT` is the extent each of the HUD zones is
 * allowed to claim. `layout.test.ts` pins that no zone's extent intersects it, so "the middle is
 * free" is arithmetic rather than a promise — and the `max-w-*` / `max-h-*` classes
 * `hudZoneClass` hands out are derived from those same rectangles, which is what stops the CSS
 * and the assertion drifting apart.
 *
 * **The rectangles are what the classes enforce, not what the content happens to be.** A zone is
 * capped in width and height; keeping the panels inside that cap is the panels' own job, and the
 * two left-hand zones deliberately do not clip (`PopulationBadge` hangs its roster panel out of
 * the top-left one, and clipping it would break a control rather than tidy a layout). What the
 * cap guarantees is the thing the player asked for: nothing in this file can put a *control* in
 * the middle of the screen.
 *
 * ── AND ONE ZONE IS INSIDE THE RECTANGLE ON PURPOSE: THE FEED ──
 *
 * This file used to say there was deliberately no bottom-centre zone at all, on the grounds that
 * the bottom of the middle column is the worst place on the screen to put anything because "swim
 * down" is a drag straight through it. That reasoning is still exactly right **about controls
 * and about panels somebody has to read a number off**, and it is wrong about a chat feed — which
 * is the one thing that was asked to be centred there, on both surfaces.
 *
 * So the rule is written down as what it always meant. `STEERING_ZONE` is a region no *pressable*
 * thing may enter, because a control there is a control a steering thumb presses by accident and
 * a drag that starts on one never reaches the canvas at all. `FEED_ONLY_ZONES` is the exemption,
 * it has exactly one member, and what qualifies for it is a hard property rather than a taste: a
 * zone that holds nothing but read-only text inside the HUD's `pointer-events-none` layer, opting
 * back in nowhere. A pointer that lands on one is a pointer the canvas still gets — the same
 * arrangement `NoticeOverlay` is already under at the top of the screen — so it costs a thumb
 * nothing but the pixels it covers, and those are bounded (see `BOTTOM_FEED_HEIGHT`).
 *
 * `layout.test.ts` asserts both halves: every zone outside the exemption clears the rectangle,
 * and the exemption has one member. A second one is a decision somebody has to make in this
 * docblock rather than a class somebody can add quietly.
 */

import { JOYSTICK_PAD_PX } from '../input/joystick';
import { useMediaQuery } from './ActionButtons';

/**
 * "This screen is too small for the full HUD."
 *
 * A comma is an `or`: either the viewport is narrower than Tailwind's `sm` breakpoint — a phone
 * upright, or a browser window dragged narrow — **or** it is shorter than `480px`, which is the
 * shape this card is really about. A phone in landscape is 320–430 CSS pixels tall and a small
 * laptop is 600–800, so 480 sits in the gap between them with room on both sides; a tablet in
 * landscape (768) and a desktop are both comfortably above it and keep the layout they have,
 * which is the half of the report that says the desktop is already good.
 *
 * `639px` is `sm - 1`, spelled out because this is `matchMedia` rather than a class — the same
 * arithmetic `TOUCH_CONTROLS_QUERY` and `ROTATE_PROMPT_QUERIES.narrow` do.
 *
 * Deliberately **not** `TOUCH_CONTROLS_QUERY`. That one asks whether there is a thumb, which is
 * the right question for whether to draw buttons and the wrong one for how big a panel should
 * be: a tablet has a thumb and plenty of room, and a short browser window has room for neither.
 */
export const COMPACT_HUD_QUERY = '(max-width: 639px), (max-height: 479px)';

/** Whether the HUD draws its tightened, edges-and-corners arrangement. */
export function useCompactHud(): boolean {
  return useMediaQuery(COMPACT_HUD_QUERY);
}

/**
 * The places a HUD panel may be: four corners, the two centre columns — and, since the thumb
 * stick, one more on the right-hand side.
 *
 * **`right-centre` USED TO BE THE ONE ZONE ALLOWED INSIDE THE STEERING RECTANGLE, AND IT IS NOT
 * ANY MORE.** The stick shipped inset — its right edge on the right rail's left edge — which put
 * it squarely in the middle of the screen and cost the whole "nothing is in the drag region"
 * rule an exception. The player's answer was that the placement was wrong rather than that the
 * rule was: "the thumb control pad with joystick should be on the very right middle ... as
 * that's where it's easy to reach with the thumb". Flush against the right edge is both what a
 * thumb wants and outside `STEERING_ZONE` entirely, so the exception is gone and
 * `layout.test.ts` asks the same question of every control zone again. It stays a zone of its
 * own because it is a control rather than a readout and because it is the one thing the right
 * rail has to end above. See `JOYSTICK_ZONE`.
 *
 * **`bottom-centre` IS THE ONE THAT IS INSIDE IT ON PURPOSE, AND IT IS NOT A CONTROL.** It holds
 * the sayings feed and nothing else may ever be put in it — see `FEED_ONLY_ZONES` and the
 * docblock at the top of this file for what qualifies and why a pressable thing never can.
 */
export type HudZone =
  | 'top-left'
  | 'top-centre'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-centre'
  | 'bottom-right'
  | 'right-centre';

/** Every zone, so a test can walk them rather than restate the list. */
export const HUD_ZONES: readonly HudZone[] = [
  'top-left',
  'top-centre',
  'top-right',
  'bottom-left',
  'bottom-centre',
  'bottom-right',
  'right-centre',
];

/**
 * The zones exempt from `STEERING_ZONE`, and the whole list of what it takes to be one.
 *
 * A zone is in here only if **everything drawn in it is read-only text that never opts back into
 * pointer events**. That is a property of the panels, not an intention about them: the HUD's
 * whole overlay is `pointer-events-none` and each control opts back in individually (the action
 * row, the gear, Leave, the stick, the Say button), so a zone none of whose children opt in is a
 * zone a steering pointer passes straight through to the canvas. `NoticeOverlay` is the same
 * arrangement at the top of the screen and `Hud.test.tsx` is where "the feed takes no pointer
 * events" is pinned rather than described.
 *
 * The one member is `bottom-centre`, which is the sayings feed. It is centred because a player
 * asked for it centred, and it is allowed to be because a line of somebody else's chat is the one
 * thing on this HUD that costs a dragging thumb nothing at all — a control there would be pressed
 * by accident on every "swim down", which is exactly what `STEERING_ZONE` exists to stop and
 * exactly what this exemption does not weaken.
 *
 * Adding a second member means arguing it in the docblock at the top of this file. `layout.test.ts`
 * pins the length, so it cannot be done quietly.
 */
export const FEED_ONLY_ZONES: readonly HudZone[] = ['bottom-centre'];

/** A rectangle in fractions of the arena box: 0 is its left/top edge and 1 its right/bottom. */
export interface HudRect {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/**
 * The part of the arena a thumb is using, which no HUD **control** may enter on a compact
 * surface — and which one feed-only zone deliberately sits inside. See `FEED_ONLY_ZONES`.
 *
 * The middle **35%** of the width, from **34%** of the height down to the bottom edge. Three
 * numbers, and each is a decision:
 *
 * - The blob sits in the middle of the screen, so a heading is chosen by putting a thumb some
 *   distance from the centre in the direction you want to go. The nearer the middle a panel that
 *   takes the pointer is, the smaller the heading it steals; the bottom-centre is the worst of
 *   all, because "swim down" is a drag straight into it. That is a statement about *pressable*
 *   things — see the exemption below and `FEED_ONLY_ZONES`.
 * - `0.34` is where the top band ends. It is what the centre column — the spectator banner, the
 *   event feed, the control hint and the status line — is capped at, so the band stays a band,
 *   and it is measured rather than chosen: the tallest that column ever gets is a spectator's
 *   banner and Join button over two notice lines, which is 124 pixels of a 390-pixel screen.
 *   Nothing there is clipped, deliberately — the Join button is the only way out of spectate
 *   mode — and every child is `shrink-0`, because a flex column that meets its own `max-height`
 *   squashes its children rather than overflowing, and a six-pixel-tall status line is worse
 *   than one that reaches a few pixels past a cap.
 *
 * - **It is symmetric — `0.35` to `0.65` — because the action row can be in EITHER bottom
 *   corner.** It used to be `0.3` to `0.65`: the row was always bottom-right, so only that side
 *   needed the wider `CONTROL_RAIL_WIDTH` allowance. The thumb stick moving to the right edge
 *   swapped the two corners over (the row went left, the map went right), and a rectangle that
 *   described one arrangement would be describing the wrong one half the time. So it is the
 *   narrower middle that BOTH arrangements leave clear, which is the only honest single answer.
 *   The row is the one zone whose size is its content rather than a class: three 48-pixel targets
 *   — Say, the power-up, Eject — and a 64-pixel Split with 4-pixel gaps measure 220 pixels, 33%
 *   of the narrowest landscape phone this layout is for (667 CSS pixels) and less on every larger
 *   one. `CONTROL_RAIL_WIDTH` is that, rounded out — and it is what kept the sprint's Run button
 *   *off* this row: a fifth control on it is 272 pixels, which is 41% of that phone and reaches
 *   back across this rectangle's own edge. It went on the secondary row instead; see
 *   `ACTION_ROW_HEIGHT`.
 *
 * The two rails are HUD territory for their whole height, and that is the honest description of
 * the layout: what is *in* them is a top cluster and a bottom cluster with nothing between, but
 * what the CSS actually guarantees is the width, so that is what is written down.
 *
 * ── NO CONTROL IS ALLOWED IN, INCLUDING THE THUMB STICK ──
 *
 * The stick shipped inside this rectangle, on the reasoning that a control which means a thumb
 * no longer has to drag belongs where the dragging was. That was a good argument for a bad
 * position: an inset stick is a thumb reaching *in* over the arena, and the player asked for it
 * on the very right edge, where a thumb already rests. Flush right puts it past `right` here, so
 * no control zone is in the drag region and every one of them is checked against this rectangle
 * in `layout.test.ts`.
 *
 * ── AND THE SAYINGS FEED IS, BECAUSE IT IS NOT A CONTROL AND CANNOT BECOME ONE ──
 *
 * `bottom-centre` is inside this rectangle by construction — it *is* the bottom of the middle
 * column — and that is the deliberate exemption `FEED_ONLY_ZONES` names. What this rectangle
 * protects is a drag, and a drag is only ever stolen by an element that takes the pointer; the
 * feed does not, so a "swim down" that starts on a line of chat still reaches the canvas and
 * still steers. What it does cost is the pixels of map underneath it, which is why it is the one
 * zone with a height budget written against the reference phone rather than against a panel that
 * happens to fit — see `BOTTOM_FEED_HEIGHT`.
 */
export const STEERING_ZONE: HudRect = { left: 0.35, top: 0.34, right: 0.65, bottom: 1 };

/** The share of the arena's width each side rail may claim. Derives `max-w-[30%]` below. */
const RAIL_WIDTH = 0.3;

/**
 * The share the action row claims in the bottom-right corner, which is wider than a rail.
 *
 * **The row is the one zone with no width cap on it, and that is deliberate.** A `max-width` on
 * an absolutely positioned row anchored to the right edge does not shrink its children — they are
 * fixed-size buttons — it makes them overflow, and the overflow goes off the *right* of the
 * screen, which is Split. A clipped Split is a control a player cannot press, and no layout rule
 * is worth that. So the row is content-sized, grows leftward out of the corner, and this number
 * is what it measures against the narrowest phone rather than what a class enforces.
 */
const CONTROL_RAIL_WIDTH = 0.35;

/** How far down the centre column may reach. Derives `max-h-[34%]` below. */
const TOP_BAND_HEIGHT = 0.34;

/**
 * How tall the action row measures in the bottom-right corner, as a share of the shortest screen
 * this layout is for.
 *
 * A secondary row (48px), a gap and a 64px Split, plus the corner's own 8px offset, on a
 * 390-pixel landscape phone. The row has no cap — see `CONTROL_RAIL_WIDTH` — so this is what it
 * *measures* rather than what a class enforces, and it exists for one reason: the thumb stick sits
 * directly above that corner, and "does not overlap the buttons" has to be arithmetic rather than
 * a hope.
 *
 * **IT WAS 0.3, AND THE SPRINT IS WHAT MOVED IT — DELIBERATELY UP RATHER THAN SIDEWAYS.** The
 * secondary row used to be the inventory slots alone at 40 pixels; the Run button is a 48-pixel
 * target on it now, so the corner measures 124 pixels rather than 116. Sideways was the
 * alternative and is the one this rejects: a fifth control on the round row measures 272 pixels,
 * which is 41% of the narrowest landscape phone this layout is for and reaches back over the edge
 * of `STEERING_ZONE`. Two points of height in a corner with nothing under it is the cheap
 * direction, which is the same argument the slots row itself was added on.
 */
const ACTION_ROW_HEIGHT = 0.32;

/**
 * The reference landscape phone every pixel measurement in this file is taken against: an iPhone
 * on its side, which is the shortest surface the arena is drawn on and therefore the one where
 * anything that does not fit does not fit first.
 */
const REFERENCE_PHONE = { width: 844, height: 390 };

/**
 * How much of the bottom of the middle column the sayings feed may have, and the one number in
 * this file that is a *ceiling on an exemption* rather than a place for something to go.
 *
 * The feed is the only thing allowed inside `STEERING_ZONE` (see `FEED_ONLY_ZONES`), and what it
 * costs a player is exactly this: map they can see through but are looking at chat over. So the
 * budget is the smallest one that fits what `saying-lines.ts` already bounds the feed at, rather
 * than a share that leaves room to grow into.
 *
 * **31%, and it is measured — it was 26%, and BOTH halves of what moved it were the player
 * asking.** They asked for the text to be bigger and for the feed not to sit flush against the
 * bottom edge, and each of those is pixels this budget has to carry or else quietly clip a line
 * `MAX_SAYING_LINES` promised. That cap is four, and a compact line is now `text-xs` at
 * `leading-snug` inside `py-1` — about 24 pixels, up from the 22 a 10px line measured — with
 * three 4-pixel gaps between them: 110 pixels, plus the 8-pixel gap under the panel, is 118 of a
 * 390-pixel screen. Four lines is also the cap, so nothing can push this: a fifth saying drops
 * the oldest rather than growing the panel. The column is `justify-end` and `overflow-hidden` all
 * the same, so if a line ever wraps it is the oldest one that gives way and never the newest.
 *
 * The width it takes is `RAIL_WIDTH` — the same 30% a side rail gets — which is what makes it
 * exactly the middle column and nothing wider; the panel itself draws narrower still (`w-36`,
 * 17% of the reference phone), so the rectangle is a truthful upper bound rather than a
 * description.
 */
const BOTTOM_FEED_HEIGHT = 0.31;

/**
 * The thumb stick's pad, in fractions of that phone.
 *
 * The pad is a fixed number of CSS pixels, because what it is sized against is a thumb rather
 * than a viewport (`../input/joystick.ts`); this is the same square expressed the way the rest
 * of this file talks, so it can be checked against the zones around it.
 */
const JOYSTICK_W = JOYSTICK_PAD_PX / REFERENCE_PHONE.width;
const JOYSTICK_H = JOYSTICK_PAD_PX / REFERENCE_PHONE.height;

/**
 * Where the stick goes: **hard against the right edge of the arena, vertically centred.**
 *
 * The player's correction, verbatim: "it's not positioned well ... the thumb control pad with
 * joystick should be on the very right middle, under the top 5 on mobile as that's where it's
 * easy to reach with the thumb. But I think the size of it is good." So the size is untouched
 * and only the anchor moved — from the rail's inner edge to the screen's outer one.
 *
 * Three things fall out of that, and the third is why this card is more than a class change:
 *
 * - **It is out of `STEERING_ZONE` now**, whose right edge is `CONTROL_RAIL_WIDTH` back from
 *   here. The inset stick needed that rectangle to carry an exception; this one does not, and
 *   the exception is deleted rather than kept as dead prose.
 * - **It is vertically centred**, which is the report's own "very right middle" and is also
 *   where a hand holding a phone in landscape actually rests.
 * - **THE RIGHT RAIL HAS TO END ABOVE IT, AND THAT IS THE REAL COST.** Flush right means the
 *   stick is *inside* the right rail's column rather than beside it, so `top-right` gains an
 *   honest bottom edge for the first time (`RIGHT_RAIL_HEIGHT`) and everything that used to
 *   fill that rail no longer fits in it. The run panel moved to the left rail — the player's own
 *   suggestion, "we might need to move the your run over to the left or something" — and the
 *   phone's leaderboard went from four rows to three. See `RIGHT_RAIL_HEIGHT`.
 *
 * Its bottom edge still clears `ACTION_ROW_HEIGHT`, so a thumb reaching for the stick can never
 * land on Split; `layout.test.ts` is what holds that, and it now also holds the new one — that
 * the rail above stops before the stick starts.
 */
export const JOYSTICK_ZONE: HudRect = {
  left: 1 - JOYSTICK_W,
  top: 0.5 - JOYSTICK_H / 2,
  right: 1,
  bottom: 0.5 + JOYSTICK_H / 2,
};

/**
 * How far down the right rail may reach on a compact surface, and the one number in this file
 * that is a *budget* rather than a measurement.
 *
 * 36% rather than the 37.7% `JOYSTICK_ZONE.top` allows, and the gap is deliberate headroom: the
 * rectangles here are fractions of the *reference* phone, and the box the classes are really
 * measured against is the HUD overlay, which is shorter than the screen whenever it is paying a
 * top safe-area inset (`.hud-safe-top`). A 96-pixel stick centred in a 366-pixel box starts at
 * 36.9% rather than 37.7%, so a cap written to the larger figure would overlap it on exactly the
 * phone this is about. `layout.test.ts` pins the relationship rather than the literal — so shrinking the
 * pad hands the rail the room back automatically, which is exactly what happened when the pad
 * came down to the ring's own bounding box, and growing it fails the test rather than silently
 * putting a panel under the stick.
 *
 * **WHAT IT COSTS IS ONE PANEL'S WORTH OF RAIL, AND THAT BILL IS PAID EXPLICITLY.** 144 pixels
 * holds the gear and Leave controls and a leaderboard, and nothing else, so the **run panel** is
 * drawn in the top-left cluster on this surface instead. On a roomy screen it stays exactly where
 * it was, beside the leaderboard.
 *
 * **AND THE LEADERBOARD IS FOUR ROWS RATHER THAN FIVE, WHICH IS MEASURED RATHER THAN GUESSED.**
 * A compact board is 116 pixels at three rows, 137 at four and 158 at five — 21 pixels a row,
 * taken in a browser at the reference phone's width. Against 144 pixels of rail, four fits with
 * six to spare and five is fourteen over, so five would not be a fifth row but a fifth row cut in
 * half: the ranked list is the declared `min-h-0 shrink` that gives way. The player's own row
 * sits in a `shrink-0` block underneath and is never one of the rows that goes.
 *
 * Four rather than three is bought by two things at once — the pad shrinking, and the gear and
 * Leave moving to the leaderboard's *side* instead of sitting above it (`RAIL_ABOVE_STICK`).
 */
const RIGHT_RAIL_HEIGHT = 0.36;

/**
 * What each zone is allowed to cover on a compact surface, as the `max-w` / `max-h` classes in
 * `hudZoneClass` enforce it.
 *
 * A rail runs the full height because that is all its width class says; the centre column is the
 * only zone bounded vertically *by the steering rule*, and it is the one that has to be.
 *
 * The two rails carry a height cap of their own all the same, and it is not about the middle of
 * the screen — it is about each other. The gear, the top four and the run come down from the top
 * of the right rail and the action row comes up from the bottom of it, and on a 390-pixel screen
 * those two meet unless something stops them. 76% is what the four panels above measure at that
 * height with about 20 pixels to spare; on anything shorter the leaderboard's ranked list is the
 * `min-h-0 shrink` that gives way, and the player's own row is deliberately not inside it.
 */
export const COMPACT_ZONE_RECT: Record<HudZone, HudRect> = {
  'top-left': { left: 0, top: 0, right: RAIL_WIDTH, bottom: 1 },
  'top-centre': { left: RAIL_WIDTH, top: 0, right: 1 - RAIL_WIDTH, bottom: TOP_BAND_HEIGHT },
  // The rail with a real bottom edge, and the thumb stick is what gave it one: flush right, the
  // stick is in this column rather than beside it. See `RIGHT_RAIL_HEIGHT`.
  'top-right': { left: 1 - RAIL_WIDTH, top: 0, right: 1, bottom: RIGHT_RAIL_HEIGHT },
  'bottom-left': { left: 0, top: 0, right: RAIL_WIDTH, bottom: 1 },
  // THE ONE ZONE INSIDE `STEERING_ZONE`, AND IT IS THE FEED. Exactly the middle column, flush to
  // the bottom edge, and nothing pressable may ever be drawn in it. Its sides land on the two
  // bottom corners' declared edges in BOTH arrangements — 0.35 is the swapped action row's right
  // edge and 0.65 is the unswapped one's left — so a shared edge is the whole clearance, which is
  // what `rectsOverlap` counts as clear. See `FEED_ONLY_ZONES` and `BOTTOM_FEED_HEIGHT`.
  'bottom-centre': {
    left: STEERING_ZONE.left,
    top: 1 - BOTTOM_FEED_HEIGHT,
    right: STEERING_ZONE.right,
    bottom: 1,
  },
  // The one rail with an honest *vertical* extent, and the thumb stick is why it grew one: it is
  // content-sized and it is what the stick has to clear. See `ACTION_ROW_HEIGHT`.
  'bottom-right': { left: 1 - CONTROL_RAIL_WIDTH, top: 1 - ACTION_ROW_HEIGHT, right: 1, bottom: 1 },
  'right-centre': JOYSTICK_ZONE,
};

/**
 * How much of the right side the readouts column may have once the stick is there — and the
 * second thing the flush-right stick made somebody pay for.
 *
 * The right-hand side is three things stacked now: the rail down to `RIGHT_RAIL_HEIGHT`, the
 * stick across the middle, and this under it. 36% is what is left below `JOYSTICK_ZONE.bottom`,
 * with the same headroom the rail above it takes for a top inset,
 * and `layout.test.ts` pins the relationship rather than the literal, exactly as it does for the
 * rail above.
 *
 * 144 pixels holds a **112-pixel minimap**, the **12-pixel stamina bar** above it and the
 * inventory strip above that, which is why the column is ordered with the map nearest the corner:
 * it is `justify-end` and `overflow-hidden`, so what gives way under pressure is what is above it
 * rather than the map somebody is navigating by.
 *
 * **THE STAMINA BAR COST THIS BUDGET NOTHING, AND THAT IS ARITHMETIC RATHER THAN LUCK.** In the
 * arrangement this rectangle is about — a compact screen with a thumb stick — the action row is
 * drawn, so `sayingsInRow` is true and the strip and the Say button are in the *other* corner: the
 * column holds the map and this bar and nothing else, which is 112 + 4 + 12 = 128 of the 136 the
 * cap allows. Nothing gave way. The one arrangement in which it is tight is a compact screen with
 * the stick on and the on-screen buttons *off*, where the strip and the Say button fall back into
 * this column — and that column was already over its budget before this card, with the strip as
 * the declared thing above the map that gives. The **sayings feed** used to lead this column and does not any more — it is
 * `bottom-centre` now, which is also what gave this budget the room it has.
 * **The run panel does not even try to fit here** — it moves to the top-left badges instead when
 * the stick is drawn, which is the one place on a compact screen with room to spare.
 */
const READOUT_COLUMN_HEIGHT = 0.36;

/**
 * The same table WITH THE TWO BOTTOM CORNERS SWAPPED, which is the arrangement a thumb stick puts
 * the HUD in.
 *
 * The player: "now that the thumb control is on the right, the buttons for split and say and all
 * that need to be on the left as they have to be pushed with the free left hand. So I'm not sure
 * if we want to move the map to the bottom right then so we're basically swapping them." That is
 * the standard shape of a two-thumb layout and it follows from the stick's move rather than being
 * a taste: one hand holds the stick, the other has to reach the buttons, and they cannot be the
 * same hand.
 *
 * So **bottom-left becomes the controls corner and bottom-right becomes the readouts corner** —
 * the action row and, when the row is switched off, the slot strip and the Say button that fall
 * back out of it; against the minimap and the sayings feed. Only the corners move: the two top
 * rails, the top band and the stick are untouched, because nothing about them is handed.
 *
 * It is a second table rather than a mirror function on the first because the two corners are not
 * mirror images — the row is content-sized and taller-than-wide, the map column is a rail — and a
 * function that pretended they were would be arithmetic nobody could check. `layout.test.ts`
 * walks BOTH tables against `STEERING_ZONE`, which is why that rectangle is symmetric now.
 */
export const MIRRORED_ZONE_RECT: Record<HudZone, HudRect> = {
  ...COMPACT_ZONE_RECT,
  'bottom-left': { left: 0, top: 1 - ACTION_ROW_HEIGHT, right: CONTROL_RAIL_WIDTH, bottom: 1 },
  'bottom-right': { left: 1 - RAIL_WIDTH, top: 1 - READOUT_COLUMN_HEIGHT, right: 1, bottom: 1 },
};

/**
 * What a zone may cover, for the arrangement the HUD is actually in.
 *
 * `stick` is the same runtime flag `hudZoneClass` takes and it decides the same thing in both
 * places — which is the point of it being one argument threaded from one place in `Hud`, rather
 * than two conditions that could disagree about which corner holds Split.
 */
export function compactZoneRect(zone: HudZone, stick = false): HudRect {
  return (stick ? MIRRORED_ZONE_RECT : COMPACT_ZONE_RECT)[zone];
}

/** Whether two rectangles share any area. Edges that merely touch do not count as overlapping. */
export function rectsOverlap(a: HudRect, b: HudRect): boolean {
  return a.left < b.right && b.left < a.right && a.top < b.bottom && b.top < a.bottom;
}

/**
 * Where a zone sits and how it stacks, per surface.
 *
 * The compact half is the edges-and-corners arrangement this card is; the roomy half is what the
 * HUD drew at `sm` and above before it, character for character where it can be — the desktop
 * layout is the one the player said was already good, and a change to it here would be a
 * regression dressed up as a refactor.
 *
 * The compact top-left is a **wrapping row** rather than a column: four short badges stacked
 * vertically reach a third of the way down a 390-pixel screen, and laid out across they are two
 * lines of the top band. Nothing else about them changes.
 */
export function hudZoneClass(zone: HudZone, compact: boolean, stick = false): string {
  const table = compact ? COMPACT_ZONE_CLASS : ROOMY_ZONE_CLASS;
  if (!stick) return table[zone];
  if (zone === 'top-right') return compact ? RAIL_ABOVE_STICK.compact : RAIL_ABOVE_STICK.roomy;
  // The two corners the stick swaps over: the hand on the stick cannot also be the hand on Split.
  if (zone === 'bottom-left' || zone === 'bottom-right') {
    return MIRRORED_ZONE_CLASS[compact ? 'compact' : 'roomy'][zone];
  }
  return table[zone];
}

/**
 * The right rail's class WHEN THE THUMB STICK IS DRAWN, which is the only time it has a bottom.
 *
 * The stick is a control on a touch surface behind a switch the player owns, so "is there a
 * stick" is a runtime fact rather than a media query — and the rail only has to get out of the
 * way when there actually is one. A desktop has no thumb and therefore never takes either of
 * these; a phone with the stick switched off keeps the taller rail it always had.
 *
 * The roomy figure is 40% rather than `RIGHT_RAIL_HEIGHT`: a tablet is 768 CSS pixels tall, so
 * the same 136-pixel pad centred on it starts at 41% instead of 33%, and a rail that took the
 * phone's budget on a tablet would be throwing away room nobody needed to save.
 */
/**
 * The two bottom corners with the controls on the LEFT and the map on the RIGHT — the arrangement
 * a thumb stick puts the HUD in. See `MIRRORED_ZONE_RECT` for why.
 *
 * Character for character the corners they replace, with `left` and `right` exchanged and the
 * cross-axis alignment with them: a column that grew from the right edge has to grow from the
 * left one, or the map and the feed would hang off the wrong side of their own rail.
 */
const MIRRORED_ZONE_CLASS = {
  compact: {
    'bottom-left': 'absolute bottom-2 left-2 flex items-end',
    'bottom-right':
      'absolute bottom-1 right-1 flex max-h-[calc(36%-0.25rem)] max-w-[calc(30%-0.25rem)] flex-col items-end justify-end gap-1 overflow-hidden [&>*]:shrink-0',
  },
  roomy: {
    'bottom-left': 'absolute bottom-6 left-6 flex items-end gap-3',
    'bottom-right': 'absolute bottom-4 right-4 flex flex-col items-end gap-2',
  },
} as const;

const RAIL_ABOVE_STICK = {
  // `flex-row-reverse` is what buys the fourth leaderboard row: the gear and Leave sit BESIDE the
  // board rather than above it, so their 36 pixels come out of the rail's width — which has 253
  // of them and needs 189 — instead of out of its height, which has 144 and needs every one. The
  // first child is still the controls, so reversing the row is what keeps them in the corner.
  compact:
    'absolute right-1 top-1 flex max-h-[calc(36%-0.25rem)] max-w-[calc(30%-0.25rem)] flex-row-reverse items-start justify-start gap-1 overflow-hidden',
  roomy:
    'absolute right-4 top-4 flex max-h-[calc(40%-1rem)] flex-col items-end gap-2 overflow-hidden',
} as const;

/**
 * **The caps subtract their own offset, and that is not fussiness.**
 *
 * A zone is placed `left-1` (0.25rem) and then capped at 30% of the arena's width, so its right
 * edge lands at *inset + 30%* — four pixels past the rail the rectangles above declare. Four
 * pixels is nothing to look at and it is the difference between `layout.test.ts` describing what
 * a browser draws and merely describing what was intended, so the offset comes back off the cap.
 * Measured in a browser at 844 x 390: every rail lands on 0.300 exactly.
 */
const COMPACT_ZONE_CLASS: Record<HudZone, string> = {
  'top-left': 'absolute left-1 top-1 flex max-w-[calc(30%-0.25rem)] flex-wrap items-start gap-1',
  'top-centre':
    'absolute inset-x-0 top-1 mx-auto flex w-fit max-h-[calc(34%-0.25rem)] max-w-[40%] flex-col items-center gap-1 [&>*]:shrink-0',
  'top-right':
    'absolute right-1 top-1 flex max-h-[76%] max-w-[calc(30%-0.25rem)] flex-col items-end gap-1 overflow-hidden',
  'bottom-left':
    'absolute bottom-1 left-1 flex max-h-[72%] max-w-[calc(30%-0.25rem)] flex-col items-start justify-end gap-1 overflow-hidden [&>*]:shrink-0',
  // NOT FLUSH ANY MORE, AND THAT REVERSES WHAT THIS COMMENT USED TO SAY. It shipped on
  // `bottom-0` because the player asked for the feed "right at bottom" on a phone; having read it
  // there they asked for the gap back, so it is `bottom-2` — the same 0.5rem the other bottom
  // corner takes off the edge it is anchored to. The height cap therefore carries a `calc` like
  // every other offset zone in this table: the rectangle's own 31% less the offset, so the panel
  // still ends on the rail rather than eight pixels past it. `justify-end` and `overflow-hidden`
  // put the pressure on the oldest line; nothing in here opts back into pointer events, which is
  // the whole of why it is allowed to be here at all.
  'bottom-centre':
    'absolute inset-x-0 bottom-2 mx-auto flex w-fit max-h-[calc(31%-0.5rem)] max-w-[30%] flex-col items-center justify-end gap-1 overflow-hidden [&>*]:shrink-0',
  'bottom-right': 'absolute bottom-2 right-2 flex items-end',
  // `right-1` is the same 0.25rem inset every other compact zone takes off the edge it is
  // anchored to; the rectangle above is written to the arena's own edge, which is the quarter of
  // a rem of slack every zone in this table already carries.
  'right-centre': 'absolute right-1 top-1/2 flex -translate-y-1/2 items-center justify-center',
};

const ROOMY_ZONE_CLASS: Record<HudZone, string> = {
  'top-left': 'absolute left-4 top-4 flex flex-col items-start gap-2',
  'top-centre':
    'absolute inset-x-0 top-4 mx-auto flex w-fit max-w-[90%] flex-col items-center gap-1.5',
  'top-right': 'absolute inset-y-4 right-4 flex flex-col items-end gap-2',
  'bottom-left': 'absolute bottom-4 left-4 flex flex-col items-start gap-2',
  // **NOT flush, and the offset is bigger than it looks because the bottom edge is taken.** On a
  // roomy screen the control hint sits at `bottom-4` and the status line at `bottom-16` — up to
  // about 104 pixels of centred, transient chrome — and they are staying there, because that is
  // the layout the player called good. So the feed is anchored above them rather than through
  // them: `bottom-28` is 112 pixels, which clears the taller of the two with room to spare. On a
  // compact screen those two are in the top band instead and the feed takes the edge itself.
  'bottom-centre':
    'absolute inset-x-0 bottom-28 mx-auto flex w-fit max-w-[40%] flex-col items-center gap-1.5',
  'bottom-right': 'absolute bottom-6 right-6 flex items-end gap-3',
  // The same place on a roomy touch surface — a tablet, where the rails are `w-44` rather than a
  // share of the width and there is room for the stick on the edge without anything moving.
  'right-centre': 'absolute right-4 top-1/2 flex -translate-y-1/2 items-center justify-center',
};
